import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, ArrowLeft, Wand2, Copy } from "lucide-react";
import { toast } from "sonner";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const randomCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < 5; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return `8020-${s}`;
};

interface VideoItem { title: string; youtube_url: string; description: string; }
interface PackageItem { name: string; price_cents: number | ""; description: string; deliverables: string[]; }
interface Form {
  slug: string;
  access_code: string;
  client_name: string;
  client_company: string;
  title: string;
  intro: string;
  videos: VideoItem[];
  packages: PackageItem[];
  cta_label: string;
  cta_url: string;
  status: "draft" | "active" | "archived";
}

const empty: Form = {
  slug: "",
  access_code: randomCode(),
  client_name: "",
  client_company: "",
  title: "",
  intro: "",
  videos: [],
  packages: [],
  cta_label: "Réserver un call",
  cta_url: "",
  status: "draft",
};

const AdminOfferEditor = () => {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(slugParam);
  const [form, setForm] = useState<Form>(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [id, setId] = useState<string | null>(null);

  useEffect(() => {
    if (!slugParam) return;
    (async () => {
      const { data, error } = await supabase.from("offers").select("*").eq("slug", slugParam).maybeSingle();
      if (error || !data) {
        toast.error("Offre introuvable");
        navigate("/admin/offres");
        return;
      }
      setId(data.id);
      setForm({
        slug: data.slug,
        access_code: data.access_code,
        client_name: data.client_name,
        client_company: data.client_company || "",
        title: data.title,
        intro: data.intro || "",
        videos: (data.videos as unknown as VideoItem[]) || [],
        packages: (data.packages as unknown as PackageItem[]) || [],
        cta_label: data.cta_label || "",
        cta_url: data.cta_url || "",
        status: data.status,
      });
      setLoading(false);
    })();
  }, [slugParam, navigate]);

  const save = async () => {
    if (!form.client_name || !form.title || !form.slug || !form.access_code) {
      toast.error("Nom client, titre, slug et code sont requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug,
        access_code: form.access_code,
        client_name: form.client_name,
        client_company: form.client_company || null,
        title: form.title,
        intro: form.intro || null,
        videos: form.videos as unknown as never,
        packages: form.packages as unknown as never,
        cta_label: form.cta_label || null,
        cta_url: form.cta_url || null,
        status: form.status,
      };
      if (isEdit && id) {
        const { error } = await supabase.from("offers").update(payload).eq("id", id);
        if (error) throw error;
        toast.success("Offre mise à jour");
        if (form.slug !== slugParam) navigate(`/admin/offres/${form.slug}`, { replace: true });
      } else {
        const { data: userData } = await supabase.auth.getUser();
        const { error } = await supabase
          .from("offers")
          .insert({ ...payload, created_by: userData.user?.id ?? null });
        if (error) throw error;
        toast.success("Offre créée");
        navigate(`/admin/offres/${form.slug}`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const copyFullLink = () => {
    const url = `${window.location.origin}/offre/${form.slug}?code=${encodeURIComponent(form.access_code)}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien complet copié");
  };

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/offres")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Retour
        </Button>
        <h1 className="text-2xl font-bold">{isEdit ? "Éditer l'offre" : "Nouvelle offre"}</h1>
      </div>

      {/* Client + slug + code */}
      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-lg">Client & accès</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Nom du client *</Label>
            <Input
              value={form.client_name}
              onChange={(e) => {
                const name = e.target.value;
                setForm((f) => ({
                  ...f,
                  client_name: name,
                  slug: f.slug || slugify(name),
                }));
              }}
            />
          </div>
          <div>
            <Label>Entreprise (optionnel)</Label>
            <Input
              value={form.client_company}
              onChange={(e) => setForm((f) => ({ ...f, client_company: e.target.value }))}
            />
          </div>
          <div>
            <Label>Slug URL *</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
              placeholder="marie-tremblay"
            />
            <p className="text-xs text-muted-foreground mt-1">/offre/{form.slug || "..."}</p>
          </div>
          <div>
            <Label>Code d'accès *</Label>
            <div className="flex gap-2">
              <Input
                value={form.access_code}
                onChange={(e) => setForm((f) => ({ ...f, access_code: e.target.value }))}
              />
              <Button type="button" variant="outline" size="icon" onClick={() => setForm((f) => ({ ...f, access_code: randomCode() }))}>
                <Wand2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        {isEdit && (
          <Button type="button" variant="outline" onClick={copyFullLink}>
            <Copy className="h-4 w-4 mr-2" /> Copier le lien complet (avec code)
          </Button>
        )}
      </Card>

      {/* Contenu */}
      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-lg">Contenu de l'offre</h2>
        <div>
          <Label>Titre de l'offre *</Label>
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ex: Production podcast — série de 6 épisodes" />
        </div>
        <div>
          <Label>Intro / description</Label>
          <Textarea
            value={form.intro}
            onChange={(e) => setForm((f) => ({ ...f, intro: e.target.value }))}
            rows={6}
            placeholder="Contexte, approche proposée, valeur ajoutée…"
          />
        </div>
      </Card>

      {/* Videos */}
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">Vidéos exemples</h2>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setForm((f) => ({ ...f, videos: [...f.videos, { title: "", youtube_url: "", description: "" }] }))
            }
          >
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </div>
        {form.videos.length === 0 && (
          <p className="text-sm text-muted-foreground">Aucune vidéo. Ajoutez des exemples YouTube pour montrer votre travail.</p>
        )}
        {form.videos.map((v, i) => (
          <div key={i} className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs font-mono text-muted-foreground">Vidéo #{i + 1}</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => setForm((f) => ({ ...f, videos: f.videos.filter((_, k) => k !== i) }))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <Input
              placeholder="Titre"
              value={v.title}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  videos: f.videos.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)),
                }))
              }
            />
            <Input
              placeholder="URL YouTube (ex: https://youtu.be/xxx)"
              value={v.youtube_url}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  videos: f.videos.map((x, k) => (k === i ? { ...x, youtube_url: e.target.value } : x)),
                }))
              }
            />
            <Textarea
              placeholder="Description courte"
              rows={2}
              value={v.description}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  videos: f.videos.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)),
                }))
              }
            />
          </div>
        ))}
      </Card>

      {/* Packages */}
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">Prix & livrables</h2>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setForm((f) => ({
                ...f,
                packages: [...f.packages, { name: "", price_cents: "", description: "", deliverables: [] }],
              }))
            }
          >
            <Plus className="h-4 w-4 mr-1" /> Ajouter un package
          </Button>
        </div>
        {form.packages.length === 0 && (
          <p className="text-sm text-muted-foreground">Aucun package. Ajoutez au moins un tarif avec ses livrables.</p>
        )}
        {form.packages.map((p, i) => (
          <div key={i} className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs font-mono text-muted-foreground">Package #{i + 1}</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => setForm((f) => ({ ...f, packages: f.packages.filter((_, k) => k !== i) }))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Input
                placeholder="Nom du package"
                value={p.name}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    packages: f.packages.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)),
                  }))
                }
              />
              <Input
                type="number"
                placeholder="Prix en $ CAD (ex: 3500)"
                value={typeof p.price_cents === "number" ? p.price_cents / 100 : ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    packages: f.packages.map((x, k) =>
                      k === i
                        ? { ...x, price_cents: e.target.value === "" ? "" : Math.round(parseFloat(e.target.value) * 100) }
                        : x,
                    ),
                  }))
                }
              />
            </div>
            <Textarea
              placeholder="Description du package"
              rows={2}
              value={p.description}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  packages: f.packages.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)),
                }))
              }
            />
            <div>
              <Label className="text-xs">Livrables (un par ligne)</Label>
              <Textarea
                rows={4}
                value={(p.deliverables || []).join("\n")}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    packages: f.packages.map((x, k) =>
                      k === i ? { ...x, deliverables: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) } : x,
                    ),
                  }))
                }
                placeholder={"6 épisodes de 30 min\nMontage complet\nExports optimisés Instagram"}
              />
            </div>
          </div>
        ))}
      </Card>

      {/* CTA + status */}
      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-lg">Bouton d'action & publication</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Texte du bouton</Label>
            <Input value={form.cta_label} onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))} />
          </div>
          <div>
            <Label>URL du bouton</Label>
            <Input
              value={form.cta_url}
              onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))}
              placeholder="https://cal.com/..."
            />
          </div>
          <div>
            <Label>Statut</Label>
            <Select value={form.status} onValueChange={(v: "draft" | "active" | "archived") => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Brouillon (non accessible)</SelectItem>
                <SelectItem value="active">Active (accessible avec le code)</SelectItem>
                <SelectItem value="archived">Archivée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-3 sticky bottom-4">
        <Button onClick={save} disabled={saving} size="lg">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Enregistrer" : "Créer l'offre"}
        </Button>
      </div>
    </div>
  );
};

export default AdminOfferEditor;
